import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';

const assessmentData = [
    {
        id: 1,
        title: "How can I prepare for the Assessment?",
        desc: "Get ready by understanding the basics, practicing regularly, and reviewing key topics. Try mock tests, manage your time well, and stay calm and confident throughout your preparation journey ahead confidently.",
    },
    {
        id: 2,
        title: "What Happens After the Technical Assessment?",
        desc: "After your technical assessment at Makers Labs, we review your performance, share feedback, and if shortlisted, invite you for final discussions, project alignment, and smooth onboarding into the team process. ",
    },
    {
        id: 3,
        title: "Why Does Mobikode Use Technical Assessments?",
        desc: "Mobikode uses technical assessments to understand real skills beyond resumes, ensuring developers can solve real problems, write clean code, and fit smoothly into teams for reliable, high-quality project delivery. ",
    },
    {
        id: 4,
        title: "What Should I Expect From The Assessment?",
        desc: "You can expect a simple, hands-on assessment that explores your thinking, creativity, and skills. It includes real tasks, practical challenges, and helpful feedback to understand how you approach problems and work. ",
    }
];

const ThoughtSection = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [loaded, setLoaded] = useState(false);

    const [sliderRef, instanceRef] = useKeenSlider(
        {
            initial: 0,
            loop: true,
            slideChanged(slider) {
                setCurrentSlide(slider.track.details.rel);
            },
            created() {
                setLoaded(true);
            },
            breakpoints: {
                "(min-width: 640px)": {
                    slides: { perView: 1.5, spacing: 20 },
                },
                "(min-width: 768px)": {
                    slides: { perView: 2, spacing: 20 },
                },
                "(min-width: 1024px)": {
                    slides: { perView: 3, spacing: 24 },
                },
            },
            slides: { perView: 1.1, spacing: 16 },
        },
        [
            (slider) => {
                let timeout;
                let mouseOver = false;
                function clearNextTimeout() {
                    clearTimeout(timeout);
                }
                function nextTimeout() {
                    clearTimeout(timeout);
                    if (mouseOver) return;
                    timeout = setTimeout(() => {
                        slider.next();
                    }, 3000);
                }
                slider.on("created", () => {
                    slider.container.addEventListener("mouseover", () => {
                        mouseOver = true;
                        clearNextTimeout();
                    });
                    slider.container.addEventListener("mouseleave", () => {
                        mouseOver = false;
                        nextTimeout();
                    });
                    nextTimeout();
                });
                slider.on("dragStarted", clearNextTimeout);
                slider.on("animationEnded", nextTimeout);
                slider.on("updated", nextTimeout);
            },
        ]
    );

    const fadeUpVariant = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    return (
        <section className="w-full pb-[40px] md:pb-[80px] font-sora bg-white overflow-hidden">
            <div className="max-w-[1440px] mx-auto px-[20px] md:px-[40px] lg:px-[80px]">

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    variants={fadeUpVariant}
                    className="mb-[24px] md:mb-[60px]"
                >
                    <h2 className="text-[20px] lg:text-[30px] font-[600] text-[#01386A] tracking-tight">
                        Thought Leadership
                    </h2>
                </motion.div>

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.1 }}
                    variants={fadeUpVariant}
                    className="relative"
                >
                    <div ref={sliderRef} className="keen-slider !overflow-visible lg:!overflow-hidden py-4">
                        {assessmentData.map((slide) => (
                            <div className="keen-slider__slide" key={slide.id}>
                                <div className="group w-full h-[280px] sm:h-[320px] p-6 sm:p-8 flex flex-col justify-between border border-transparent bg-[#F0F0F0] transition-all duration-[400ms] ease-in-out relative cursor-pointer md:hover:bg-[#01386A] md:hover:border-[#01386A] md:hover:shadow-xl rounded-sm">

                                    <div>
                                        <h3 className="text-[16px] lg:text-[20px] font-[600] font-sora mb-3 sm:mb-4 leading-snug text-[#01386A] md:group-hover:text-white transition-colors duration-[400ms]">
                                            {slide.title}
                                        </h3>

                                        <p className="text-[14px] lg:text-[16px] font-graphik font-medium leading-relaxed text-[#6C7689] md:group-hover:text-white/80 transition-colors duration-[400ms] line-clamp-4">
                                            {slide.desc}
                                        </p>
                                    </div>

                                    <div className="pt-4 flex items-center mt-auto">
                                        <span className="inline-flex items-center text-[14px] lg:text-[16px] font-[500] tracking-wide font-sora text-[#01386A] md:group-hover:text-white transition-colors duration-[400ms]">
                                            <span className="relative after:absolute after:bottom-0 after:left-0 after:h-[1.5px] after:w-full after:origin-bottom-right after:scale-x-0 after:transition-transform after:duration-300 md:group-hover:after:origin-bottom-left md:group-hover:after:scale-x-100 pb-0.5 after:bg-[#01386A] md:group-hover:after:bg-white">
                                                Read More
                                            </span>

                                            <svg
                                                className="w-4 h-4 ml-2 text-[#F97316] transition-transform duration-300 ease-out transform md:group-hover:translate-x-1.5"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.5"
                                                viewBox="0 0 24 24"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                                            </svg>
                                        </span>
                                    </div>

                                    <div className="absolute bottom-0 left-0 w-full h-[4px] bg-transparent md:group-hover:bg-[#ED6800] transition-colors duration-[400ms]"></div>
                                    <div className="absolute bottom-0 left-0 w-0 h-[4px] bg-[#ED6800] transition-all duration-[400ms] ease-out group-hover:w-full z-10"></div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {loaded && instanceRef.current && (
                        <div className="flex items-center gap-4 mt-6 md:mt-8">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    instanceRef.current?.prev();
                                }}
                                className="w-10 h-10 rounded-full border border-[#F97316] text-[#F97316] hover:bg-[#F97316] hover:text-white flex items-center justify-center transition-colors duration-300"
                                aria-label="Previous slide"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M19 12H5M12 19l-7-7 7-7" />
                                </svg>
                            </button>

                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    instanceRef.current?.next();
                                }}
                                className="w-10 h-10 rounded-full border border-[#F97316] text-[#F97316] hover:bg-[#F97316] hover:text-white flex items-center justify-center transition-colors duration-300"
                                aria-label="Next slide"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M5 12h14M12 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>
                    )}
                </motion.div>

            </div>
        </section>
    );
};

export default ThoughtSection;